import { useEffect, useState, type ReactNode } from "react";
import { ScreenScaffold, type ScreenState } from "../ScreenScaffold.tsx";
import { ChartSyncProvider, DenseTable, type Column } from "../../components/index.ts";
import { KpiCard } from "../../components/observability/index.ts";
import { useIdentity, roleAtLeast } from "../../state/identity.tsx";
import { useTimeRange } from "../../state/time-range.tsx";
import { useDataQuality } from "../../state/data-quality.tsx";
import { LivePauseControl } from "../../chrome/LivePauseControl.tsx";
import { TimeRangeControl } from "../../chrome/TimeRangeControl.tsx";
import { formatDateTime, formatTimeOfDay, isoAttr } from "../../lib/datetime.ts";
import {
  useMcpGatewayAccess,
  useMcpTools,
  useMcpToolDetail,
  useMcpToolSurface,
  useMcpToolSurfaceDrift,
  type McpGatewayAccessItem,
  type McpGatewayAccessPage,
  type McpToolPage,
  type McpToolSummary,
  type McpWindow,
  type PassportState,
} from "./use-mcp-gateway.ts";
import {
  ACCESS_OUTCOME_LABELS,
  GATEWAY_MODE_LABELS,
  PASSPORT_STATE_LABELS,
  SOURCE_KIND_LABELS,
  gatewayTiles,
  passportStateLabel,
  passportStateLevel,
  quantityMeasure,
  quantityValue,
  formatCount,
  formatMs,
  formatRate,
  outcomeLabel,
} from "./vocab.ts";
import { ToolDrawer } from "./ToolDrawer.tsx";
import { ToolSurfaceTab } from "./ToolSurfaceTab.tsx";
import { estimateSuffix, StatusBadge } from "../../components/index.ts";
import { PASSPORT_STATE } from "@oathera/contract";
import "./mcp-gateway.css";

type Tab = "tools" | "surface" | "access";

/**
 * The MCP Gateway dashboard (task 7.2; design §10, requirement 13.2). It composes the regions the
 * design calls for over four `viewer`-level REST reads (handlers wired in task 4.6):
 *
 * - the five headline tiles (calls, deny rate, tool error rate, p95 latency, unenforced tools),
 *   rolled up from the tools page and the tool-surface page;
 * - a tools table from `GET /api/v1/mcp/tools`, each row opening the per-tool drawer; and
 * - a Tool-surface tab (inventory + drift) from `GET /api/v1/mcp/tool-surface` and
 *   `/mcp/tool-surface/drift`; and
 * - an Access tab (telemetry contract 1.7, oathera-passport-governance Requirement 13.2) from
 *   `GET /api/v1/mcp/gateway-access`: the zero-filled Oathera Passport-state summary, a filter over
 *   the generated `passport_state` members, and the access observations. A `warn` state renders as a
 *   warning beside the authoritative outcome even when the call was forwarded; a missing state reads
 *   "Not reported" and is never counted as `other`. The Access read only runs while its tab is open.
 *
 * The tabs select which region is on screen; the per-tool drawer is driven by local selection
 * state and only fetches while a tool is selected. The screen's top-level state is driven by the
 * TOOLS page (the primary snapshot); the surface and drift reads show their own loading/empty state
 * inside their tab so a slow surface read never blanks the page. Every window comes from the shared
 * time-range, so pausing freezes every read (design 12.2, FR-13 AC2).
 *
 * It handles every state a screen must (design 12.3): restricted for a below-viewer role,
 * loading/error/empty/ready as the tools query resolves, with a cold-core waking error deferring to
 * the shell's banner (kept "loading" under it so there is no contradiction).
 */
export function McpGatewayScreen(): ReactNode {
  const { role } = useIdentity();
  const { live } = useTimeRange();
  const [tab, setTab] = useState<Tab>("tools");
  const [selectedTool, setSelectedTool] = useState<string | null>(null);
  const [passportFilter, setPassportFilter] = useState<PassportState | null>(null);

  const { query: tools, window } = useMcpTools();
  const surface = useMcpToolSurface();
  const drift = useMcpToolSurfaceDrift();
  const toolDetail = useMcpToolDetail(selectedTool);
  const access = useMcpGatewayAccess(passportFilter, tab === "access");

  const state = resolveState(role, tools);

  return (
    <ScreenScaffold
      title="MCP Gateway"
      subtitle="Per-tool call outcomes, latency and the tool-surface inventory and drift for every upstream behind the Gateway MCP."
      state={state}
    >
      {tools.data !== undefined && (
        <McpGatewayBody
          page={tools.data}
          surface={surface}
          drift={drift}
          window={window}
          live={live}
          tab={tab}
          onTabChange={setTab}
          selectedTool={selectedTool}
          onSelectTool={setSelectedTool}
          toolDetail={toolDetail}
          access={access}
          passportFilter={passportFilter}
          onPassportFilterChange={setPassportFilter}
        />
      )}
    </ScreenScaffold>
  );
}

/** Decide which scaffold state to show from the role and the tools query status. */
function resolveState(
  role: ReturnType<typeof useIdentity>["role"],
  query: ReturnType<typeof useMcpTools>["query"],
): ScreenState {
  if (!roleAtLeast(role, "viewer")) {
    return {
      kind: "restricted",
      reason:
        role === "unknown"
          ? "Your role has not been resolved yet, so the MCP Gateway view is hidden until it is. The server enforces access regardless."
          : "The MCP Gateway view needs at least the viewer role.",
    };
  }

  if (query.data !== undefined) {
    // An empty tools page with no surfaced tools is a genuinely bare window. The tools table and the
    // Tool-surface tab both still render honest empties, so the ready view is the more useful one —
    // but when there is truly nothing at all, show the explanatory empty state.
    if (query.data.items.length === 0) {
      return {
        kind: "empty",
        cause:
          "No MCP tools were forwarded in this window. Widen the time range, or check that the Gateway MCP is reporting tool calls.",
      };
    }
    return { kind: "ready" };
  }

  if (query.isError) {
    if (query.error.isWaking) return { kind: "loading" };
    return { kind: "error", message: errorMessage(query.error) };
  }

  return { kind: "loading" };
}

function errorMessage(error: ReturnType<typeof useMcpTools>["query"]["error"]): string {
  if (error === null) return "The MCP Gateway view could not load.";
  switch (error.kind) {
    case "unauthorized":
      return "Your session has expired. Reload to sign in again.";
    case "forbidden":
      return "You are not permitted to see MCP Gateway data. The server enforces access.";
    case "network":
      return "The MCP Gateway request could not be sent. Check your connection.";
    default:
      return error.message;
  }
}

function McpGatewayBody(props: {
  page: McpToolPage;
  surface: ReturnType<typeof useMcpToolSurface>;
  drift: ReturnType<typeof useMcpToolSurfaceDrift>;
  window: McpWindow;
  live: boolean;
  tab: Tab;
  onTabChange: (tab: Tab) => void;
  selectedTool: string | null;
  onSelectTool: (tool: string | null) => void;
  toolDetail: ReturnType<typeof useMcpToolDetail>;
  access: ReturnType<typeof useMcpGatewayAccess>;
  passportFilter: PassportState | null;
  onPassportFilterChange: (state: PassportState | null) => void;
}): ReactNode {
  const { page } = props;
  const generatedAt = page.meta.generated_at;

  // Keep the persistent data-quality strip consistent with this snapshot (design 12.2, NFR-4), the
  // same way the System/Overview screens do. Only the fields the response carries are reported.
  const { report } = useDataQuality();
  const dq = page.meta?.data_quality;
  const dropsInRange = dq?.drops_in_range ?? 0;
  const samplingInEffect = dq?.sampling_in_effect ?? false;
  const gapCount = (dq?.gaps ?? []).length;
  useEffect(() => {
    report({
      drops: { atSource: 0, atEdge: 0, inPipeline: dropsInRange },
      sampling: { active: samplingInEffect, rate: null, exact: !samplingInEffect },
      hasSequenceGap: gapCount > 0,
    });
  }, [report, dropsInRange, samplingInEffect, gapCount]);

  const unenforced = props.surface.query.data?.tools_unenforced ?? null;
  const tiles = gatewayTiles(page, unenforced);

  return (
    <div className="mcp" data-live={props.live}>
      <div className="screen-meta-row">
        <LivePauseControl />
        <TimeRangeControl />
        <p className="mcp__meta">
          {props.live ? "Live" : "Paused"} · window{" "}
          <time dateTime={isoAttr(props.window.fromMs)}>{formatTimeOfDay(props.window.fromMs)}</time>{" "}
          to <time dateTime={isoAttr(props.window.toMs)}>{formatTimeOfDay(props.window.toMs)}</time> ·
          generated <time dateTime={isoAttr(generatedAt)}>{formatTimeOfDay(generatedAt)}</time>
        </p>
      </div>

      {/* Headline tiles rolled up from the tools page (+ the surface's unenforced count). */}
      <section className="mcp-tiles" aria-label="MCP Gateway headline metrics">
        <div className="mcp-tiles__grid">
          {tiles.map((t) => (
            <KpiCard
              key={t.key}
              label={t.label}
              value={t.empty ? "—" : t.value}
              measure={t.measure}
            />
          ))}
        </div>
      </section>

      <div className="mcp-tabs" role="tablist" aria-label="MCP Gateway views">
        <button
          type="button"
          role="tab"
          className="mcp-tab"
          aria-selected={props.tab === "tools"}
          onClick={() => props.onTabChange("tools")}
        >
          Tools
        </button>
        <button
          type="button"
          role="tab"
          className="mcp-tab"
          aria-selected={props.tab === "surface"}
          onClick={() => props.onTabChange("surface")}
        >
          Tool surface
        </button>
        <button
          type="button"
          role="tab"
          className="mcp-tab"
          aria-selected={props.tab === "access"}
          onClick={() => props.onTabChange("access")}
        >
          Access
        </button>
      </div>

      {props.tab === "tools" && <ToolsTable page={page} onSelectTool={props.onSelectTool} />}
      {props.tab === "surface" && (
        <ChartSyncProvider>
          <ToolSurfaceTab surface={props.surface.query} drift={props.drift} />
        </ChartSyncProvider>
      )}
      {props.tab === "access" && (
        <GatewayAccessTab
          query={props.access}
          filter={props.passportFilter}
          onFilterChange={props.onPassportFilterChange}
        />
      )}

      {/* The per-tool drawer: rendered only while a tool is selected; its own query drives it. */}
      <ChartSyncProvider>
        <ToolDrawer
          tool={props.selectedTool}
          onClose={() => props.onSelectTool(null)}
          query={props.toolDetail}
        />
      </ChartSyncProvider>
    </div>
  );
}

/**
 * The tools table (requirement 13.2). One row per forwarded tool with its calls, error rate, latency
 * percentiles and a compact outcome mix; clicking a row opens the per-tool drawer.
 */
function ToolsTable(props: {
  page: McpToolPage;
  onSelectTool: (tool: string) => void;
}): ReactNode {
  const columns: readonly Column<McpToolSummary>[] = [
    { key: "tool", header: "Tool", isRowHeader: true, cell: (r) => <code>{r.tool}</code> },
    {
      key: "calls",
      header: "Calls",
      align: "right",
      cell: (r) => (
        <span data-measure={quantityMeasure(r.calls)}>
          {formatCount(r.calls)}
          {estimateSuffix(quantityMeasure(r.calls))}
        </span>
      ),
    },
    {
      key: "error",
      header: "Error rate",
      align: "right",
      cell: (r) => <span data-measure={quantityMeasure(r.error_rate)}>{formatRate(r.error_rate)}</span>,
    },
    { key: "p50", header: "p50", align: "right", cell: (r) => formatMs(r.latency_p50_ms) },
    { key: "p95", header: "p95", align: "right", cell: (r) => formatMs(r.latency_p95_ms) },
    { key: "p99", header: "p99", align: "right", cell: (r) => formatMs(r.latency_p99_ms) },
    { key: "mix", header: "Outcome mix", cell: (r) => <OutcomeMix tool={r} /> },
  ];

  return (
    <section className="mcp-tools" aria-label="Forwarded tools">
      <DenseTable
        caption="Forwarded MCP tools with calls, outcomes, latency and error rate"
        columns={columns}
        rows={props.page.items}
        rowKey={(r) => r.tool}
        stickyHeader
        onRowClick={(r) => props.onSelectTool(r.tool)}
        rowLabel={(r) => `Open the detail for tool ${r.tool}`}
        emptyMessage="No MCP tools were forwarded in this window."
      />
    </section>
  );
}

/** A compact, non-zero outcome-mix summary for a tool row (the full breakdown lives in the drawer). */
function OutcomeMix(props: { tool: McpToolSummary }): ReactNode {
  const parts = props.tool.outcome_mix
    .map((c) => ({ label: outcomeLabel(c.category), count: quantityValue(c.count) ?? 0 }))
    .filter((p) => p.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, 4);

  if (parts.length === 0) return <span className="mcp-mix__empty">—</span>;

  return (
    <span className="mcp-mix">
      {parts.map((p) => (
        <span key={p.label} className="mcp-mix__chip">
          {p.label} <strong>{p.count.toLocaleString()}</strong>
        </span>
      ))}
    </span>
  );
}

/** The `<select>` value that means "no `passport_state` filter". */
const ALL_STATES = "";

/**
 * The Access tab (telemetry contract 1.7; Requirement 13.2). The summary is zero-filled over the
 * generated `PASSPORT_STATE` members (a member the server did not return reads 0), and it counts every
 * observation in the window regardless of the filter. The filter options come from the same generated
 * array, so the UI never offers a value the contract does not define.
 */
function GatewayAccessTab(props: {
  query: ReturnType<typeof useMcpGatewayAccess>;
  filter: PassportState | null;
  onFilterChange: (state: PassportState | null) => void;
}): ReactNode {
  const { query } = props;
  const page = query.data;

  return (
    <div className="mcp-access">
      <PassportStateSummary page={page} />

      <section className="mcp-surface__section" aria-label="Gateway access observations">
        <header className="mcp-surface__header">
          <h3 className="mcp-surface__title">Access observations</h3>
          <label className="mcp-access__filter">
            Passport state
            <select
              value={props.filter ?? ALL_STATES}
              onChange={(e) => {
                const v = e.target.value;
                props.onFilterChange(
                  (PASSPORT_STATE as readonly string[]).includes(v) ? (v as PassportState) : null,
                );
              }}
            >
              <option value={ALL_STATES}>All states</option>
              {PASSPORT_STATE.map((s) => (
                <option key={s} value={s}>
                  {PASSPORT_STATE_LABELS[s]}
                </option>
              ))}
            </select>
          </label>
        </header>
        {page === undefined && query.isError ? (
          <p className="mcp-surface__state" data-kind="error">
            The Gateway access observations could not load.
          </p>
        ) : page === undefined ? (
          <p className="mcp-surface__state" role="status" aria-live="polite">
            Loading Gateway access observations…
          </p>
        ) : (
          <AccessTable items={page.items} />
        )}
      </section>
    </div>
  );
}

/** The five-state Passport summary, zero-filled over the generated members. */
function PassportStateSummary(props: { page: McpGatewayAccessPage | undefined }): ReactNode {
  const counts = new Map(
    (props.page?.passport_state_summary ?? []).map((s) => [s.passport_state, s.count]),
  );
  return (
    <section className="mcp-surface__section" aria-label="Passport state summary">
      <h3 className="mcp-surface__title">Oathera Passport state</h3>
      <ul className="mcp-access__summary">
        {PASSPORT_STATE.map((s) => {
          const count = counts.get(s);
          const value = props.page === undefined ? null : (quantityValue(count) ?? 0);
          return (
            <li key={s} className="mcp-access__summary-item" data-passport-state={s}>
              <StatusBadge
                level={passportStateLevel(s)}
                label={PASSPORT_STATE_LABELS[s]}
                size="sm"
              />
              <strong data-measure={count === undefined ? "exact" : quantityMeasure(count)}>
                {value === null
                  ? "—"
                  : value.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                {count !== undefined && estimateSuffix(quantityMeasure(count))}
              </strong>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** The access-observation table. Outcome is authoritative; the Passport state sits beside it. */
function AccessTable(props: { items: readonly McpGatewayAccessItem[] }): ReactNode {
  const columns: readonly Column<McpGatewayAccessItem>[] = [
    {
      key: "ts",
      header: "Time",
      isRowHeader: true,
      cell: (r) => <time dateTime={isoAttr(r.ts)}>{formatDateTime(r.ts)}</time>,
    },
    { key: "gateway", header: "Gateway", cell: (r) => <code>{r.gateway_id}</code> },
    { key: "mode", header: "Mode", cell: (r) => GATEWAY_MODE_LABELS[r.mode] },
    {
      key: "source",
      header: "Source",
      cell: (r) => (
        <span>
          {SOURCE_KIND_LABELS[r.source_kind]}
          {r.agent_sub !== null && (
            <>
              {" "}
              <code>{r.agent_sub}</code>
            </>
          )}
          {r.agent_sub === null && r.claimed_sub !== null && (
            <>
              {" "}
              <code>{r.claimed_sub}</code>
            </>
          )}
        </span>
      ),
    },
    { key: "target", header: "Target", cell: (r) => <code>{r.target_id}</code> },
    {
      key: "tool",
      header: "Tool",
      cell: (r) => ((r.tool ?? r.operation) !== null ? <code>{r.tool ?? r.operation}</code> : "—"),
    },
    { key: "outcome", header: "Outcome", cell: (r) => ACCESS_OUTCOME_LABELS[r.outcome] },
    {
      key: "passport",
      header: "Passport state",
      cell: (r) => (
        <span
          className="mcp-access__state"
          data-passport-state={r.passport_state ?? "not_reported"}
        >
          <StatusBadge
            level={passportStateLevel(r.passport_state)}
            label={passportStateLabel(r.passport_state)}
            size="sm"
          />
        </span>
      ),
    },
  ];

  return (
    <DenseTable
      caption="Gateway access observations with Oathera Passport state"
      columns={columns}
      rows={props.items}
      rowKey={(r) => r.observation_id}
      stickyHeader
      emptyMessage="No Gateway access observations in this window."
    />
  );
}
